/*
 * Copyright (c) 2019
 *    Reliant Business Valuation, LLC - http://reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild, ComponentFactoryResolver,
  HostListener, forwardRef, AfterViewInit, DoCheck, OnDestroy} from '@angular/core';
import {fromEvent, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map} from 'rxjs/operators';
import {SearchableProvider} from '../../../model/interface/searchable.provider';
import {SearchableItem} from 'src/app/model/interface/searchable.item';
import {ScrollToService} from '@nicky-lenaers/ngx-scroll-to';
import {SearchableSelection, } from '../../../model/interface/searchable.selection';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { CreateNewClientModal, CreateNewClientUserModal, CreateNewFranchiseModal, UserNoPositionModal } from '../../modal';
import { User, Client, ClientUser, Privilege, Role, Stage, State, ReportingPreset,
  Franchise, Position, Helpers, Template, Report, RPi, IBISReport} from 'src/app/model';
import {ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {CreateNewTypes} from '../../../model/enums/create.type';
import {PresetValue} from '../../../model/preset-value';
import {ReportingService, ModeService, UserService, ThemeService,LogService} from 'src/app/services';
import { Tag } from 'src/app/model/tag';
import { SystemType } from 'src/app/model/enums/system.type';
import { Naics } from 'src/app/model/naics';
import { Sic } from 'src/app/model/sic';
import { RoT } from 'src/app/model/rot';
import { transition, style, animate, trigger } from '@angular/animations';
import { CannedResponse } from 'src/app/modules/manage/model/canned-response/canned-response';
import { CensusGeo } from 'src/app/modules/research/model/census.geo';
import { IbisReport } from 'src/app/modules/research/model';
import { Rma } from 'src/app/model/rma';

const leaveTrans = transition(':leave', [
  style({
    opacity: 1
  }),
  animate('1s ease-out', style({
    opacity: 0
  }))
]);

const fadeOut = trigger('fadeOut', [
  leaveTrans
]);

const noop = (): any => {};

@Component({
  selector: 'input-search',
  templateUrl: 'input-search.component.html',
  styleUrls: ['input-search.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputSearchComponent),
      multi: true
    }
  ],
  animations: [
    fadeOut
  ]
})
export class InputSearchComponent implements OnInit, ControlValueAccessor, AfterViewInit, DoCheck, OnDestroy {
  @ViewChild('rootElement') rootElement: ElementRef;
  @ViewChild('inputText') inputRef: ElementRef;
  @ViewChild('container') containerRef: ElementRef;
  @Input() items: SearchableItem[] = [];
  @Input() preset: SearchableItem[] = [];
  @Input() regular = false;
  @Input() searchable = false;
  @Input() required = false;
  @Input() provider: SearchableProvider = null;
  @Input() placeholder = 'Type anything you want';
  @Input('no-drop-icon') isNoDropIcon = false;
  @Input() showCreate = false;
  @Input() createNew: CreateNewTypes;
  @Input('parent-id') parentId;
  @Input() parent: Client = new Client();
  @Input() disabled = false;
  @Input() name: string;
  @Input() presetHeader: string = null;
  @Input() itemsHeader: string = null;
  @Input() userInputAllowed = false;
  @Input() showManage = false;
  @Input() refresh = false;
  // Value to store in ngModel
  @Input() inputValue: any;
  @Input() multiple = false;
  @Input() arrValue: any[] = [];
  @Input() invalid = false;
  @Input() warningSource = false;
  @Input() typeEmail = false;
  @Input() showEmail = false;
  @Input() showValue = false;
  @Input() position: string = null;
  @Input() highligtValue: any = '';
  @Input() displayFull = false;
  @Input() userIcon = false;
  @Input() nonBank = false;
  @Input() lsp = false;
  @Input() others = false;
  @Input() paginated = false;
  @Input() typeOverride: SystemType;
  @Input() showCopy: boolean = true;
  @Input() noCancel: boolean = false;
  @Input() linkReportType: boolean = false;
  @Input() isWidth: boolean = false;
  @Input() spaceDisabled: boolean = false;
  @Input() region : string ='';
  @Output() valueChange: EventEmitter<SearchableSelection> = new EventEmitter();
  @Output() searchTriggered: EventEmitter<string> = new EventEmitter();
  @Output() multipleValueChanged: EventEmitter<SearchableItem[]> = new EventEmitter();

  searchQuery = '';

  private onChange: (_: any) => void = noop;
  private onTouched: () => void = noop;

  isOpen = false;
  selectedIndex: any = null;
  createModal: BsModalRef;
  positionModal: BsModalRef;
  topFlag = false;
  isLoading = false;
  selected = false;
  page = 1;
  totalItems = 0;
  isLoadMore = false;
  isErrorState = false;
  currentMode: SystemType;
  modeSub: any;
  currentUser: User;
  private currentUserSub: any;
  loadMoreLoading = false;
  copiedClicked: boolean = false;

  constructor(
    private _scrollToService: ScrollToService,
    private resolver: ComponentFactoryResolver,
    private reportingService: ReportingService,
    private modalService: BsModalService,
    private modeService: ModeService,
    private userService: UserService,
    private logService : LogService
  ) {}

  get value(): any {
    return this.inputValue;
  }

  set value(val: any) {
    this.inputValue = val;
    this.onChange(val);
  }

  registerOnChange(fn): void {
    this.onChange = fn;
  }

  registerOnTouched(fn): void {
    this.onTouched = fn;
  }

  writeValue(value: any): void {
    this.inputValue = value;
  }

  ngOnInit(): void {
    this.modeSub = this.modeService.mode$.subscribe(mode => this.currentMode = mode);
    this.currentUserSub = this.userService.user$.subscribe(currentUser => {
      this.currentUser = currentUser;
    });
  }

  ngOnDestroy(): void {
    if(this.modeSub)
      this.modeSub.unsubscribe();
    if(this.currentUserSub)
      this.currentUserSub.unsubscribe();
  }

  ngDoCheck(): void {
    if (this.inputValue)
      this.selected = true;
  }

  ngAfterViewInit(): void {
    if (this.regular && this.provider) {
      this.searchForQuery('');
    } else if ((!this.regular && (this.provider || !this.provider)) || (this.regular && this.searchable)) {
      const eventStream = fromEvent(this.inputRef.nativeElement, 'keyup')
        .pipe(
          map(() => this.searchQuery),
          debounceTime((this.regular && this.searchable) ? 0 : 300),
          distinctUntilChanged());

      eventStream.subscribe(input => {
        this.searchTriggered.emit(input);
        if (input) {
          if (!this.isNoDropIcon) {
            this.page = 1;
            this.isLoadMore = false;
            this.isOpen = true;
            this.searchForQuery(input);
          }
        } else this.isOpen = false;
      });
    }

    if (this.inputValue)
      this.selected = true;
  }

  getValue(value: any): any {
    if (this.preset) {
      for (const v of this.preset){
        if (v.getValue() === value)
          if (this.showValue)
            return v.getValue();
          else if (this.displayFull)
            return v.getFullDetails();
          else
            return v.getName();
      }
    }

    if (this.items) {
      for (const v of this.items)
        if (v.getValue() === value)
          return this.displayFull ? !this.linkReportType ? v.getFullDetails() : v.getValue().getReportType() : v.getName();
    }

    if (this.displayFull) {
      if (typeof value.getFullDetails === 'function')
        return value.getFullDetails();
    }

    if (typeof value.getName === 'function')
      return value.getName();

    return value;
  }

  getEmailValue(value: any): any {
    if (this.preset) {
      for (const v of this.preset){
        if (v.getValue() === value)
            return v.getValue()?.EMAIL;
      }
    }

    if (this.items) {
      for (const v of this.items)
        if (v.getValue() === value)
          return v.getValue()?.EMAIL;
    }

    if (typeof value.getName === 'function')
      return value.getValue()?.EMAIL;

    return value;
  }

  getEmail(value: any): any {
    if (this.provider.getSearchType() === 'CLIENT_USER') {
      return (<ClientUser> value).EMAIL;
    }
    return null;
  }

  onSelected(selected: SearchableItem): void {
    if (this.position && !selected.allowed) {
      this.positionModal = this.modalService.show(UserNoPositionModal, Helpers.getModalConfig());
      const ackSubject = new Subject<boolean>();
      this.positionModal.content.subject = ackSubject;
      this.positionModal.content.user = selected;
      this.positionModal.content.position = this.position;
      ackSubject.subscribe(value => {
        if (!value) { this.logService.log(value); }
      });

    } else {
      if (this.multiple) {
        this.arrValue.push(selected);
        this.multipleValueChanged.emit(this.arrValue);

        this.isOpen = false;
        this.selected = true;

        // Clear Search Query
        if (!this.userInputAllowed) {
          this.searchQuery = '';
        }

        // Propagate ngModel Change
        this.onChange(this.arrValue);

      } else {
        // Emit Event that item is Selected
        this.valueChange.emit(new SearchableSelection(selected));

        // Close Modal
        this.isOpen = false;
        this.selected = true;

        // Clear Search Query
        if (!this.userInputAllowed) {
          this.searchQuery = '';
        }

        // Write Value
        this.writeValue(selected.getValue());

        // Propagate ngModel Change
        this.onChange(selected.getValue());
      }
    }
  }

  onShow(): void {
    if (!this.disabled) {
      // with noCancel == true, clear the selected Value when Arrow Down was clicked
      if (this.noCancel){
        this.onClear();
        this.items = [];
        this.clearSelected();
      }


      this.isOpen = !this.isOpen;

      if (!this.regular && !this.isNoDropIcon) {
        if (this.items.length === 0 || this.refresh) {
          this.page = 1;
          this.isLoadMore = false;
          this.searchForQuery('');
        }
      }

      if (this.isOpen) {
        if (this.isInViewport()) {
          this.topFlag = false;
        } else {
          this.topFlag = true;
        }
      }
    }
  }

  onClear(): void {
    // Clear Search Query
    this.searchQuery = '';

    // Write Value
    this.writeValue(null);

    this.selectedIndex = null;
    this.selected = false;

    // Emit Event that item is reset
    this.valueChange.emit(null);
  }

  searchForQuery(query: string): void {
    // Related to Parent, if its User or Client,
    // this param will be ignored in implementation of interface
    if (this.provider) {
      if (!this.isLoadMore)
        this.isLoading = true;

      if(this.isLoadMore)
        this.loadMoreLoading = true;

      this.isErrorState = false;
      this.provider.search(query, this.parentId, this.nonBank, this.lsp, this.page, this.position, this.typeOverride ? this.typeOverride : this.currentMode , this.region)
        .subscribe(data => {
          if (data.success) {
            switch (this.provider.getSearchType()) {
            case 'USER':
              if (this.position) {

                let tempItems = User.parseArray(data.result.items).map(u => {
                  if (u.hasPosition(this.position)) {
                    u.allowed = true;
                  }
                  return u;
                });

                if(this.isLoadMore)
                  this.appendItems(tempItems);
                else
                  this.items = tempItems;

              } else {
                if (this.isLoadMore)
                  this.appendItems(User.parseArray(data.result.items));
                else
                  this.items = User.parseArray(data.result.items);
              }
              break;
            case 'CLIENT':
              if (this.isLoadMore)
                this.appendItems(Client.parseArray(data.result.items));
              else
                this.items = Client.parseArray(data.result.items);
              break;
            case 'CLIENT_USER':
              if (this.isLoadMore)
                this.appendItems(ClientUser.parseArray(data.result.items));
              else
                this.items = ClientUser.parseArray(data.result.items);
              break;
            case 'PRIVILEGE':
              this.items = Privilege.parseArray(data.result.items);
              break;
            case 'ROLE':
              this.items = Role.parseArray(data.result.items);
              break;
            case 'STAGE':
              this.items = Stage.parseArray(data.result);
              break;
            case 'STATE':
              this.items = State.parseArray(data.result);
              break;
            case 'FRANCHISE':
              this.items = Franchise.parseArray(data.result.items);
              break;
            case 'PRESET':
              this.items = ReportingPreset.parseArray(data.result.items);
              break;
            case 'POSITION':
              this.items = Position.parseArray(data.result.items);
              break;
            case 'TEMPLATE':
              this.items = Template.parseArray(data.result);
              break;
            case 'TAG':
              this.items = Tag.parseArray(data.result.items);
              break;
            case 'REPORT':
              this.items = Report.parseArray(data.result.items);
              break;
            case 'NAICS':
              if (this.isLoadMore)
                this.appendItems(Naics.parseArray(data.result.items));
              else
                this.items = Naics.parseArray(data.result.items);
              break;
            case 'SIC':
              if(this.isLoadMore)
                this.appendItems(Sic.parseArray(data.result.items));
              else
                this.items = Sic.parseArray(data.result.items);
              break;
            case 'RPI':
              if(this.isLoadMore)
                this.appendItems(RPi.parseArray(data.result.items));
              else
                this.items = RPi.parseArray(data.result.items);
              break;
            case 'ROT':
              if(this.isLoadMore)
                this.appendItems(RoT.parseArray(data.result.items));
              else
                this.items = RoT.parseArray(data.result.items);
              break;
            case 'IBIS':
              if(this.isLoadMore)
                this.appendItems(IBISReport.parseArray(data.result.items));
              else
                this.items = IBISReport.parseArray(data.result.items);
              break;
            case "CANNED_RESPONSE":
              this.items = CannedResponse.parseArray(data.result.items);
              break;
            case "LINKED_REPORT":
              this.items = Report.parseArray(data.result);
              break;
            case "CENSUS":
              this.items = [];
              if(data.result?.response)
                this.items = CensusGeo.parseArray(data.result.response);
              break;
              case "RMA":
                this.items = Rma.parseArray(data.result)
                break;
            default:
              break;
            }
            this.totalItems = data.result?.totalItems;
            this.isLoading = false;
            this.isErrorState = false;
            this.loadMoreLoading = false;
          }
        }, () => {
          // Don't forget to clear loading when on error
          this.isLoading = false;
          this.isErrorState = true;
          this.loadMoreLoading = false;
        });
    } else if (this.regular && this.searchable) {
      if (query.length > 0)
        this.items = this.preset.filter(item => item.getName().toLowerCase().indexOf(query.toLowerCase()) > -1);
      else this.items = [];
    }
  }

  @HostListener('document:click', ['$event.target'])
  clickedOutside(targetElement: any): void {
    const clickedInside = this.rootElement.nativeElement.contains(targetElement);
    if (!clickedInside)
      this.isOpen = false;
  }

  onKeyArrowUp(event: any): void {
    event.preventDefault();

    if (this.items) {
      if (this.items.length > 0) {
        if (this.selectedIndex === 0) {
          this.selectedIndex = null;
          this.items[0].active = false;
          return;
        }

        if (this.selectedIndex !== null) {
          // Increment Index
          this.selectedIndex--;

          // 'Over', clear rest
          for (let i = 0; i < this.items.length; i++)
            this.items[i].active = this.selectedIndex === i;

          if (this.items[this.selectedIndex]) {
            this._scrollToService.scrollTo({
              target: 'ITEMS_' + this.selectedIndex,
              container: 'DROPDOWN_ITEMS',
              duration: 650,
              easing: 'easeOutElastic',
              offset: - 25 * 5
            });
          }
        }
      }
    }
  }

  onKeyArrowDown(event: any): void {
    event.preventDefault();

    if (this.items) {
      if (this.items.length > 0) {
        if (this.selectedIndex === null) {
          this.selectedIndex = 0;
          this.items[0].active = true;
          return;
        } else {
          if ( this.selectedIndex < this.items.length - 1 ) {
            this.selectedIndex++;

            for (let i = 0; i < this.items.length; i++) {
              this.items[i].active = this.selectedIndex === i;
            }

            if (this.items[this.selectedIndex]) {
              this._scrollToService.scrollTo({
                target: 'ITEMS_' + this.selectedIndex,
                container: 'DROPDOWN_ITEMS',
                duration: 650,
                easing: 'easeOutElastic',
                offset: - 25 * 5
              });
            }
          }
        }
      }
    }
  }

  onKeyEnter(event: any): void {
    if (this.typeEmail) {
      this.onKeySpace(event);
    } else {
      event.preventDefault();

      if (this.selectedIndex !== null && this.items) {
        if (this.items[this.selectedIndex])
          this.onSelected(this.items[this.selectedIndex]);
      } else if (this.userInputAllowed) {
        this.onSelected(PresetValue.create(this.searchQuery, this.searchQuery));
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onKeySpace(event: any): void {
    if(!this.spaceDisabled){
      if (this.typeEmail) {
        const cl = new ClientUser();
        cl.EMAIL = this.searchQuery;
        cl.NAME = this.searchQuery;
        this.arrValue.push(cl);
        this.searchQuery = '';
  
        this.multipleValueChanged.emit(this.arrValue);
      }
      else {
        event.preventDefault();
        if(this.userInputAllowed && !this.multiple){
           this.onSelected(PresetValue.create(this.searchQuery.trimEnd(), this.searchQuery.trimEnd()));
        }
      }
    }
  }

  @HostListener('mouseleave')
  @HostListener('blur')
  mouseleave(): void {
    if (this.userInputAllowed && !this.selected && !this.disabled) {
      this.onSelected(PresetValue.create(this.searchQuery, this.searchQuery));
    }
  }

  createNewAction(): void {
    switch (this.createNew) {
    case CreateNewTypes.CLIENT:
      this.createModal = this.modalService.show(CreateNewClientModal, Helpers.getModalConfig());
      this.createModal.content.compact = true;
      break;
    case CreateNewTypes.CLIENT_USER:

      this.createModal = this.modalService.show(CreateNewClientUserModal, Helpers.getModalConfig());

      if (this.searchQuery && !this.inputValue) {
        const sNames = this.searchQuery;
        this.createModal.content.clientUser.NAME = sNames ? sNames : null;
      }

      if (this.parent && !this.others)
        this.createModal.content.clientUser.CLIENT = this.parent;

      if (this.others || !this.parent)
        this.createModal.content.isLender = false;
      else
        this.createModal.content.isLender = true;

      if(this.others)
        this.createModal.content.isOther = true;
      break;
      case CreateNewTypes.FRANCHISE:
        this.createModal = this.modalService.show(CreateNewFranchiseModal, Helpers.getModalConfig());
        this.createModal.content.compact = true;
        break;
    default:
      this.createModal = null;
      this.logService.error(`Unknown Create Type used [${this.createNew}]`);
      break;
    }

    if (this.createModal) {
      const ackSubject = new Subject<SearchableItem>();
      this.createModal.content.subject = ackSubject;
      ackSubject.subscribe(value => {
        if (value) {
          if (!this.items)
            this.items = [];

          this.items.push(value);
          this.onSelected(value);
        }
      });
    } else this.logService.error('Unknown Create Type used or Parent ID not Specified');
  }

  isInViewport(): boolean {
    if (this.rootElement.nativeElement) {
      const bounding = this.rootElement.nativeElement.getBoundingClientRect();
      return (
        bounding.top >= 0 &&
        bounding.bottom + 200 <= (window.innerHeight || document.documentElement.clientHeight));
    }

    return false;
  }

  onManage(): void {
    this.reportingService.onManage.emit();
  }

  removeSelected(index: number): void {
    this.arrValue.splice(index, 1);
    this.multipleValueChanged.emit(this.arrValue);
  }

  @HostListener('scroll', ['$event'])
  onScrollHandler(): void {
    if (this.containerRef.nativeElement.scrollHeight - this.containerRef.nativeElement.scrollTop === this.containerRef.nativeElement.clientHeight) {
      if (this.totalItems > this.items.length && this.paginated) {
        this.page += 1;
        this.isLoadMore = true;
        this.searchForQuery(this.searchQuery);
      }
    }
  }

  appendItems(dataItem: SearchableItem[]): void {
    for (const im of dataItem) {
      this.items.push(im);
    }
  }

  isHidden(): boolean {
    if (this.regular && this.items && this.items.length > 0) {
      return true;
    } else if (this.regular && this.preset && this.preset.length > 0) {
      return true;
    } else if (!this.regular) {
      return true;
    }

    return false;
  }

  /* To copy any Text */
  copyText(): void {
    let selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = this.getValue(this.inputValue);
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);

    if(!this.copiedClicked){
      this.copiedClicked = true;
      setTimeout(() => {
        this.copiedClicked = false;
      }, 2000);
    }
  }

  // Clear Selected Preset Items
  clearSelected(): void {
    if(this.preset && this.preset.length > 0){
      this.preset.map(p => p.active = false);
    }
  }
}
